// frontend/src/contexts/CurrencyContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Define currency options with symbols and conversion rates
// These rates would ideally come from an API in production
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  rate: number; // Conversion rate from base currency (NGN)
}

export const currencies: Currency[] = [
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', rate: 1 },
  { code: 'USD', name: 'US Dollar', symbol: '$', rate: 0.00065 },
  { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.00060 },
  { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.00051 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', rate: 0.00089 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 0.00097 },
];

interface CurrencyContextType {
  currency: Currency;
  setCurrencyByCode: (code: string) => void;
  formatPrice: (amount: number) => string;
  convertPrice: (amount: number) => number;
  availableCurrencies: Currency[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Get saved currency from localStorage or use NGN as default
  const [currency, setCurrency] = useState<Currency>(() => {
    const savedCode = localStorage.getItem('currencyCode');
    return currencies.find(c => c.code === savedCode) || currencies[0];
  });

  // Set currency by code
  const setCurrencyByCode = (code: string) => {
    const newCurrency = currencies.find(c => c.code === code);
    if (newCurrency) {
      setCurrency(newCurrency);
    }
  };

  // Format price with currency symbol
  const formatPrice = (amount: number): string => {
    // Round to 2 decimal places
    const convertedAmount = convertPrice(amount);
    
    // Format with thousand separators and appropriate decimals
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.code,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedAmount);
    
    return formatted;
  };

  // Convert price from base currency (NGN) to selected currency
  const convertPrice = (amount: number): number => {
    return amount * currency.rate;
  };

  // Save currency preference
  useEffect(() => {
    localStorage.setItem('currencyCode', currency.code);
  }, [currency]);

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrencyByCode, 
      formatPrice,
      convertPrice,
      availableCurrencies: currencies
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;